import type { DiagramSpec } from "../types"

/** Compound nested — use cases feed into vertical pipeline groups, converge to output */
export const compoundDiagram: DiagramSpec = {
  id: "compound",
  name: "Compound: Use Cases → Pipeline Groups",
  description: "Use cases connect to vertical pipeline groups that converge to shared output",
  layers: [
    { id: "usecase", name: "Use Case", color: "#818cf8" },
    { id: "group", name: "Pipeline", color: "#64748b" },
    { id: "step", name: "Step", color: "#6366f1" },
    { id: "output", name: "Output", color: "#10b981" },
  ],
  nodes: [
    // ── Use Cases at top ──
    {
      id: "uc-edu",
      label: "Education",
      icon: "📚",
      layer: "usecase",
      rank: 0,
      position: { x: 40, y: 0 },
      description: "Tutorials, explainers, how-to",
    },
    {
      id: "uc-mkt",
      label: "Marketing",
      icon: "📢",
      layer: "usecase",
      rank: 0,
      position: { x: 300, y: 0 },
      description: "Product demos, ads, brand",
    },
    {
      id: "uc-ent",
      label: "Entertainment",
      icon: "🎭",
      layer: "usecase",
      rank: 0,
      position: { x: 600, y: 0 },
      description: "Faceless channels, viral",
    },
    {
      id: "uc-soc",
      label: "Social",
      icon: "📱",
      layer: "usecase",
      rank: 0,
      position: { x: 860, y: 0 },
      description: "Shorts, Reels, TikTok",
    },

    // ── Pipeline Group 1: Talking Head (vertical) ──
    {
      id: "g-th",
      label: "Talking Head",
      icon: "🗣️",
      layer: "group",
      isGroup: true,
      width: 240,
      height: 460,
      rank: 1,
      position: { x: 0, y: 130 },
    },
    {
      id: "th-script",
      label: "Script",
      layer: "step",
      parentNode: "g-th",
      description: "Claude Sonnet",
      icon: "✍️",
      position: { x: 20, y: 50 },
    },
    {
      id: "th-voice",
      label: "Voice",
      layer: "step",
      parentNode: "g-th",
      description: "ElevenLabs",
      icon: "🎙️",
      position: { x: 20, y: 150 },
    },
    {
      id: "th-video",
      label: "Video",
      layer: "step",
      parentNode: "g-th",
      description: "Seedance",
      icon: "🎬",
      position: { x: 20, y: 250 },
    },
    {
      id: "th-edit",
      label: "Auto-Edit",
      layer: "step",
      parentNode: "g-th",
      description: "NanoBanana",
      icon: "✂️",
      position: { x: 20, y: 350 },
    },

    // ── Pipeline Group 2: Animation (vertical) ──
    {
      id: "g-an",
      label: "Animation",
      icon: "🎨",
      layer: "group",
      isGroup: true,
      width: 240,
      height: 360,
      rank: 1,
      position: { x: 300, y: 130 },
    },
    {
      id: "an-script",
      label: "Script",
      layer: "step",
      parentNode: "g-an",
      description: "Claude Sonnet",
      icon: "✍️",
      position: { x: 20, y: 50 },
    },
    {
      id: "an-video",
      label: "Video",
      layer: "step",
      parentNode: "g-an",
      description: "Veo 3",
      icon: "🎬",
      position: { x: 20, y: 150 },
    },
    {
      id: "an-edit",
      label: "Auto-Edit",
      layer: "step",
      parentNode: "g-an",
      description: "NanoBanana",
      icon: "✂️",
      position: { x: 20, y: 250 },
    },

    // ── Pipeline Group 3: Slideshow (vertical) ──
    {
      id: "g-sl",
      label: "Slideshow",
      icon: "🖼️",
      layer: "group",
      isGroup: true,
      width: 240,
      height: 360,
      rank: 1,
      position: { x: 600, y: 130 },
    },
    {
      id: "sl-script",
      label: "Script",
      layer: "step",
      parentNode: "g-sl",
      description: "Claude Sonnet",
      icon: "✍️",
      position: { x: 20, y: 50 },
    },
    {
      id: "sl-voice",
      label: "Voice",
      layer: "step",
      parentNode: "g-sl",
      description: "ElevenLabs",
      icon: "🎙️",
      position: { x: 20, y: 150 },
    },
    {
      id: "sl-edit",
      label: "Auto-Edit",
      layer: "step",
      parentNode: "g-sl",
      description: "NanoBanana",
      icon: "✂️",
      position: { x: 20, y: 250 },
    },

    // ── Pipeline Group 4: Shorts (vertical) ──
    {
      id: "g-sh",
      label: "Shorts",
      icon: "⚡",
      layer: "group",
      isGroup: true,
      width: 240,
      height: 360,
      rank: 1,
      position: { x: 900, y: 130 },
    },
    {
      id: "sh-script",
      label: "Script",
      layer: "step",
      parentNode: "g-sh",
      description: "Claude Sonnet",
      icon: "✍️",
      position: { x: 20, y: 50 },
    },
    {
      id: "sh-video",
      label: "Video",
      layer: "step",
      parentNode: "g-sh",
      description: "Runway Gen-3",
      icon: "🎬",
      position: { x: 20, y: 150 },
    },
    {
      id: "sh-edit",
      label: "Auto-Edit",
      layer: "step",
      parentNode: "g-sh",
      description: "NanoBanana",
      icon: "✂️",
      position: { x: 20, y: 250 },
    },

    // ── Shared outputs ──
    {
      id: "youtube",
      label: "YouTube",
      icon: "📺",
      layer: "output",
      position: { x: 160, y: 650 },
      description: "Long-form + Shorts",
    },
    {
      id: "tiktok",
      label: "TikTok",
      icon: "📱",
      layer: "output",
      position: { x: 460, y: 650 },
      description: "Vertical clips",
    },
    {
      id: "instagram",
      label: "Instagram",
      icon: "📸",
      layer: "output",
      position: { x: 760, y: 650 },
      description: "Reels + carousel",
    },
  ],
  edges: [
    // Use cases → pipelines
    { id: "uc-e1", source: "uc-edu", target: "g-th", label: "primary" },
    { id: "uc-e2", source: "uc-edu", target: "g-sl", style: "dashed" },
    { id: "uc-e3", source: "uc-mkt", target: "g-th" },
    { id: "uc-e4", source: "uc-mkt", target: "g-an" },
    { id: "uc-e5", source: "uc-ent", target: "g-an" },
    { id: "uc-e6", source: "uc-ent", target: "g-sl" },
    { id: "uc-e7", source: "uc-soc", target: "g-sh" },
    { id: "uc-e8", source: "uc-soc", target: "g-th", style: "dashed" },

    // Internal pipeline flows
    { id: "e-th1", source: "th-script", target: "th-voice" },
    { id: "e-th2", source: "th-voice", target: "th-video" },
    { id: "e-th3", source: "th-video", target: "th-edit" },
    { id: "e-an1", source: "an-script", target: "an-video" },
    { id: "e-an2", source: "an-video", target: "an-edit" },
    { id: "e-sl1", source: "sl-script", target: "sl-voice" },
    { id: "e-sl2", source: "sl-voice", target: "sl-edit" },
    { id: "e-sh1", source: "sh-script", target: "sh-video" },
    { id: "e-sh2", source: "sh-video", target: "sh-edit" },

    // Pipelines → outputs
    { id: "e-out1", source: "th-edit", target: "youtube" },
    { id: "e-out2", source: "an-edit", target: "youtube" },
    { id: "e-out3", source: "an-edit", target: "tiktok" },
    { id: "e-out4", source: "sl-edit", target: "youtube" },
    { id: "e-out5", source: "sh-edit", target: "tiktok" },
    { id: "e-out6", source: "sh-edit", target: "instagram" },
    { id: "e-out7", source: "th-edit", target: "instagram", style: "dashed" },
  ],
  views: [{ id: "main", name: "Full System", layout: "manual" }],
  defaultView: "main",
  preferences: {
    nodeWidth: 180,
    showLayerBands: false,
    edgeStyle: "smoothstep",
    edgeColor: "#4a6a8a",
    animateEdges: true,
  },
}
